/**
 * WordPress dependencies
 */
import { select, dispatch } from '@wordpress/data';

/**
 * External dependencies
 */
import isEqual from 'lodash/isEqual';
import once from 'lodash/once';
import type { Uuid } from 'uuid';
import type { TrackEvent } from '@nab/types';

/**
 * Internal dependencies
 */
import { store as NAB_CHECKER } from './store';
import type { MissingSessionRationale } from '../public/types';
import { addCookieListener, getCookie } from '../public/utils/cookies';
import { getExperimentsWithPageViews } from '../public/utils/tracking';
import { getSegmentationSettings } from '../public/utils/segmentation/segmentation-settings';

export const addListeners = once( () => {
	// @ts-expect-error This event type is defined in assets/src/public/tracking/index.ts
	window.addEventListener( 'nelio-ab-testing/bootstrap', onBootstrap );
	window.addEventListener( 'nelio-ab-testing/variant-ready', onVariantReady );
	window.addEventListener( 'nelio-ab-testing/content-ready', onContentReady );
	// @ts-expect-error This event type is defined in assets/src/public/tracking/index.ts
	window.addEventListener(
		'nelio-ab-testing/gdpr-status',
		onGdprStatusChange
	);
	// @ts-expect-error This event type is defined in assets/src/public/tracking/index.ts
	window.addEventListener( 'nelio-ab-testing/track', onEventTrack );
	addCookieListener( onCookiesChanged );
} );

// =======
// HELPERS
// =======

function onBootstrap( ev: CustomEvent< MissingSessionRationale > ) {
	void dispatch( NAB_CHECKER ).setStatus( {
		type: 'bootstrap',
		detail: ev.detail,
	} );
}

function onVariantReady() {
	window.removeEventListener(
		'nelio-ab-testing/content-ready',
		onContentReady
	);
	void dispatch( NAB_CHECKER ).setStatus( {
		type: 'content-ready',
		isTestVariant: true,
	} );
}

function onContentReady() {
	void dispatch( NAB_CHECKER ).setStatus( {
		type: 'content-ready',
		isTestVariant: false,
	} );
}

function onGdprStatusChange( ev: CustomEvent< 'ready' | 'awaiting' > ) {
	void dispatch( NAB_CHECKER ).setGdprStatus( ev.detail );
}

function onEventTrack(
	ev: CustomEvent< { readonly event: TrackEvent & { readonly id: Uuid } } >
) {
	const event = ev.detail.event;

	switch ( event.kind ) {
		case 'visit':
			void dispatch( NAB_CHECKER ).addViewEvent( {
				id: event.id,
				experiment: event.experiment,
				alternative: event.alternative,
			} );
			break;

		case 'conversion':
			void dispatch( NAB_CHECKER ).addConversionEvent( {
				id: event.id,
				experiment: event.experiment,
				alternative: event.alternative,
				goal: event.goal,
			} );
			break;

		case 'click':
			void dispatch( NAB_CHECKER ).addClickEvent( {
				id: event.id,
				experiment: event.experiment,
				alternative: event.alternative,
				selector: event.xpath,
				coords: [ event.x, event.y ],
			} );
			break;

		case 'scroll':
		case 'uconversion':
		case 'uvisit':
			// Do nothing.
			break;
	}
}

function onCookiesChanged() {
	const alternative = getAlternativeCookie();
	const segmentation = getSegmentationSettings();
	const views = getExperimentsWithPageViews();

	if (
		undefined !== alternative &&
		select( NAB_CHECKER ).getAlternative() !== alternative
	) {
		void dispatch( NAB_CHECKER ).setAlternative( alternative );
	}

	if ( ! isEqual( segmentation, select( NAB_CHECKER ).getSegmentation() ) ) {
		void dispatch( NAB_CHECKER ).setSegmentation( segmentation );
	}

	if ( ! isEqual( views, select( NAB_CHECKER ).getViews() ) ) {
		void dispatch( NAB_CHECKER ).setViews( views );
	}
}

function getAlternativeCookie(): undefined | number {
	const value = getCookie( 'nabAlternative' );
	if ( undefined === value ) {
		return value;
	}

	if ( 'none' === value ) {
		return undefined;
	}

	const alternative = Number.parseInt( value );
	if ( Number.isNaN( alternative ) ) {
		return undefined;
	}

	return alternative;
}
